import { useAuth } from "@clerk/clerk-react";
import {
  Avatar,
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { NewCampaignProvider } from "contexts/NewCampaign";
import { OrganizationUserContext } from "contexts/Organization";
import { useContext, useEffect, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";

import StyledLink from "components/Styled/Link";
import { HOW_TO_USE_CAMPAIGN_VIDEO_LINK } from "constants/influencer";
import NewCampaign from "features/Influencer/Campaigns/NewCampaign";
import { fetcherAuth } from "utils/api";

import StyledProPage from "../StyledPage";

interface Props {
  handleNext: () => void;
  handleBack: () => void;
  onFinishSetUpLater?: () => void;
}

const BRAND_COLORS = ["#5C6BC0", "#26A69A", "#EF5350", "#FF7043", "#66BB6A"];

const BRANDS = [
  { name: "Luma Beauty", color: BRAND_COLORS[0], instagram: "@lumabeauty" },
  { name: "Nomad Gear", color: BRAND_COLORS[1], instagram: "@nomad.gear" },
  { name: "Everly Home", color: BRAND_COLORS[2], instagram: "@everlyhome" },
  {
    name: "Frost Athletics",
    color: BRAND_COLORS[3],
    instagram: "@frostathletics",
  },
  {
    name: "Groundwork Coffee",
    color: BRAND_COLORS[4],
    instagram: "@groundwork.coffee",
  },
];

const BrandCard = ({
  name,
  color,
  checked,
  instagram,
}: {
  name: string;
  color: string;
  checked: boolean;
  instagram: string;
}) => {
  const initials = name
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <Paper
      variant="outlined"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderRadius: 2,
        borderColor: checked ? "primary.main" : grey[200],
        backgroundColor: checked ? "primary.50" : "white",
        cursor: "default",
      }}
    >
      <Checkbox checked={checked} size="small" disableRipple sx={{ p: 0 }} />
      <Avatar
        sx={{
          bgcolor: color,
          width: 36,
          height: 36,
          fontSize: 13,
          fontWeight: 700,
        }}
      >
        {initials}
      </Avatar>
      <Box>
        <Typography sx={{ fontWeight: 500, fontSize: 14 }}>{name}</Typography>
        <Typography sx={{ fontSize: 12, color: grey[500] }}>
          {instagram}
        </Typography>
      </Box>
      <Chip
        label="Matched"
        size="small"
        sx={{
          ml: "auto",
          fontSize: 11,
          backgroundColor: "#E8F5E9",
          color: "#2E7D32",
          fontWeight: 500,
        }}
      />
    </Paper>
  );
};

const SelectBrandsStep = () => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 3, overflow: "hidden", width: "100%" }}
  >
    <Box
      sx={{
        px: 2.5,
        py: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: `1px solid ${grey[200]}`,
      }}
    >
      <StepTitle number="1" label="Search & Pitch Brands" />
      <FormControlLabel
        control={<Checkbox checked={true} size="small" disableRipple />}
        label={
          <Typography sx={{ fontSize: 13, color: grey[600] }}>
            Select all
          </Typography>
        }
        sx={{ m: 0, gap: 0.5 }}
      />
    </Box>
    <Grid container direction="column" gap={1} sx={{ p: 2 }}>
      {BRANDS.map((brand, i) => (
        <BrandCard
          key={brand.name}
          name={brand.name}
          color={brand.color}
          checked={i < 3}
          instagram={brand.instagram}
        />
      ))}
    </Grid>
    <Box
      sx={{
        px: 2.5,
        py: 1.5,
        borderTop: `1px solid ${grey[200]}`,
        backgroundColor: grey[50],
      }}
    >
      <Typography sx={{ fontSize: 12, color: grey[500] }}>
        3 of 5 brands selected
      </Typography>
    </Box>
  </Paper>
);

const FakeField = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: string;
}) => (
  <Box>
    <Typography
      sx={{
        fontSize: 11,
        fontWeight: 600,
        color: grey[500],
        mb: 0.5,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {label}
    </Typography>
    <Paper
      variant="outlined"
      sx={{
        px: 1.5,
        py: 1.25,
        borderRadius: 2,
        backgroundColor: grey[50],
        display: "flex",
        alignItems: "center",
        gap: 1,
      }}
    >
      <Box
        component="i"
        className={icon}
        sx={{ color: grey[400], fontSize: 14, width: 16, textAlign: "center" }}
      />
      <Typography sx={{ fontSize: 14, color: grey[700] }}>{value}</Typography>
    </Paper>
  </Box>
);

const CreateCampaignStep = () => (
  <Paper
    variant="outlined"
    sx={{ borderRadius: 3, overflow: "hidden", width: "100%" }}
  >
    <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${grey[200]}` }}>
      <StepTitle number="2" label="Fill in Campaign Details" />
    </Box>
    <Grid container direction="column" gap={2} sx={{ p: 2.5 }}>
      <FakeField
        label="Campaign Name"
        value="Spring Outreach 2026"
        icon="fa-regular fa-tag"
      />
      <FakeField
        label="Start Date"
        value="April 1, 2026"
        icon="fa-regular fa-calendar"
      />
      <FakeField
        label="Brands Sent Per Week"
        value="20 brands / week"
        icon="fa-regular fa-paper-plane"
      />
      <FakeField
        label="Timezone"
        value="America / New York"
        icon="fa-regular fa-clock"
      />
    </Grid>
  </Paper>
);

const StepTitle = ({ number, label }: { number: string; label: string }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
    <Box
      sx={{
        width: 24,
        height: 24,
        borderRadius: "50%",
        backgroundColor: "black",
        color: "white",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: 12,
        fontWeight: 700,
        flexShrink: 0,
      }}
    >
      {number}
    </Box>
    <Typography sx={{ fontWeight: 600, fontSize: 15 }}>{label}</Typography>
  </Box>
);

const ProOnboardingCampaignForm = ({
  handleBack,
  handleNext,
  onFinishSetUpLater,
}: Props) => {
  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { currentOrg } = useContext(OrganizationUserContext);
  const [hasCampaign, setHasCampaign] = useState(false);

  const checkIfHasCampaign = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/campaigns/exist`,
      );

      setHasCampaign(res.hasCampaign);
    } catch (error) {
      // Ignore error
    }
  };

  useEffect(() => {
    if (currentOrg?.id) {
      checkIfHasCampaign();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);
  return (
    <>
      <StyledProPage
        stepNumber={3}
        title={
          hasCampaign ? "Learn: Campaigns" : "Create Your First Campaign 🚀"
        }
        description={
          "Pitch hundreds of brands in one click and use campaign analytics to see what's working and improve your results."
        }
        body={
          <Box sx={{ width: "100%" }}>
            <Grid
              container
              item
              xs={12}
              gap={2}
              alignItems="flex-start"
              sx={{ width: "100%", mt: 2 }}
            >
              <Grid item xs>
                <SelectBrandsStep />
              </Grid>
              <Grid item xs>
                <CreateCampaignStep />
              </Grid>
            </Grid>
            <Grid item xs={12} container justifyContent={"center"}>
              <Box
                sx={{
                  backgroundColor: "purple.light",
                  px: 3,
                  py: 1.5,
                  borderRadius: 8,
                  my: 2,
                }}
              >
                💭 Want to watch a video tutorial?{" "}
                <StyledLink href={HOW_TO_USE_CAMPAIGN_VIDEO_LINK}>
                  Check this out
                </StyledLink>
              </Box>
            </Grid>
          </Box>
        }
        onNext={() => {
          if (hasCampaign) {
            handleNext();
            return;
          }
          navigate("new");
        }}
        onBack={handleBack}
        onFinishSetUpLater={onFinishSetUpLater}
        buttonNext={hasCampaign ? "Next" : "Create A Campaign"}
        buttonBack={"Back"}
        splitScreen={false}
      />

      <Routes>
        <Route
          path="new/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
        <Route
          path="campaign/:campaignId/*"
          element={
            <NewCampaignProvider>
              <NewCampaign />
            </NewCampaignProvider>
          }
        />
      </Routes>
    </>
  );
};

export default ProOnboardingCampaignForm;
